/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { Measure } from '@opengeoweb/theme';
import { MapControlButton, MapMeasure } from '@opengeoweb/webmap-react';
import { useCoreTranslation } from '../../../utils/i18n';

interface MeasureToolButtonProps {
  isActive: boolean;
  setIsActive: (isActive: boolean) => void;
}

export const MeasureToolButton: React.FC<MeasureToolButtonProps> = ({
  isActive,
  setIsActive,
}) => {
  const { t } = useCoreTranslation();

  React.useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent): void => {
      if (event.isTrusted && document.activeElement === document.body) {
        if (event.key === 'm' || event.key === 'M') {
          setIsActive(!isActive);
        }
      }
    };
    document.addEventListener('keyup', handleKeyPress);
    return (): void => {
      document.removeEventListener('keyup', handleKeyPress);
    };
  }, [isActive, setIsActive]);

  return (
    <>
      <MapControlButton
        data-testid="measure-tool-button"
        isActive={isActive}
        onClick={() => setIsActive(!isActive)}
        title={t('map-measure-tool')}
      >
        <Measure />
      </MapControlButton>
      {isActive ? <MapMeasure /> : null}
    </>
  );
};
